import React from 'react'
import classNames from 'classnames'
import { BasicCardProps } from './types.d'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import { textLinkIconType } from '../TextLink/TextLink.d'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { BasicCardOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

const BasicCard = ({
  bgColor,
  cardBorderColor,
  ctaLabel,
  ctaLink,
  elevation,
  icon,
  linkIcons = 'none',
  paddingMatrix,
  sectionDescription,
  sectionHeading,
  subtitle,
  textAlignment,
  toggleSwitch, // inline content
  dataTestId = 'BasicCard-1'
}: BasicCardProps) => {
  const renderAriaHidden = () => {
    return icon && icon[0].url?.toLowerCase().includes('.svg')
      ? { 'aria-hidden': true }
      : {}
  }
  const { language, defaultLanguage, generateDefaultLanguagePage } =
    useI18next()
  const languageContext = {
    language,
    defaultLanguage,
    generateDefaultLanguagePage
  }
  const cardClasses = classNames(
    bgColor,
    Card.borderColor(cardBorderColor),
    Card.cardPadding(paddingMatrix[0]),
    Card.elevation(elevation),
    Text.alignment(textAlignment),
    'rounded-md',
    {
      border: cardBorderColor !== 'null',
      'md:flex': toggleSwitch,
      'md:justify-center md:items-center':
        toggleSwitch && textAlignment === 'center'
    }
  )
  const descriptionClasses = classNames('text-gray-100', {
    'md:mx-2': toggleSwitch && textAlignment === 'center'
  })

  return (
    <div className={cardClasses} data-testid={dataTestId}>
      {icon ? (
        <picture>
          <img
            alt={
              icon[0].url?.toLowerCase().includes('.svg')
                ? ''
                : icon[0].imageAlt
            }
            {...renderAriaHidden()}
            className='inline mb-3 md:mb-0 min-h-[30px]'
            data-testid='BasicCard-icon'
            src={icon[0].url}
          />
        </picture>
      ) : null}
      {sectionHeading ? (
        <Typography as='h4' classes='text-default mb-3' variation='h4'>
          {sectionHeading}
        </Typography>
      ) : null}
      {subtitle ? (
        <p
          className='text-gray-125 font-semibold mb-2'
          data-testid='BasicCard-subheading'
        >
          {subtitle}
        </p>
      ) : null}
      {sectionDescription ? (
        <div className={descriptionClasses} data-testid='BasicCard-description'>
          {renderRichText(
            sectionDescription,
            BasicCardOptions(languageContext)
          )}
        </div>
      ) : null}
      {ctaLink && ctaLabel ? (
        <TextLink
          classes={`${linkIcons} !leading-[24px] mt-2`}
          href={ctaLink}
          icon={linkIcons as textLinkIconType}
          text={ctaLabel}
        />
      ) : null}
    </div>
  )
}

export default BasicCard
